import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { HttpService } from '@Services/index';
import { useBoundStore } from '@Store/useBoundStore';
import type { AuthUserOnboardingStatus } from '@Store/slices/authOnboarding/authOnboarding.types';

interface SetPasswordRequest {
  password: string;
}
export interface SetPasswordResponse {
  token: string;
  accessToken?: string;
}

export interface CompleteOnboardingResponse {
  token: string;
  success: boolean;
  onboardingStatus: AuthUserOnboardingStatus;
  docvRequired: boolean;
  url?: string;
  qrCode?: string;
}

export const setAuthorizedUserPassword = async (data: SetPasswordRequest) => {
  const { customerId, authorizedUserId } = useBoundStore.getState().onboardingSlice;
  const token = useBoundStore.getState().getAuthUserAccessToken();

  if (!customerId) {
    const url = `v3/onboarding/authorized/user/${authorizedUserId}/password`;
    const res = await HttpService.post<SetPasswordResponse>(url, {
      password: data.password,
      accessToken: token,
    });
    return res.data;
  }

  const url = `v3/onboarding/user/${customerId}/authorized/user/${authorizedUserId}/password`;

  const res = await HttpService.post<SetPasswordResponse>(url, {
    setPasswordData: {
      password: data.password,
    },
    token,
  });

  return res.data;
};

export const useSetAuthorizedUserPassword = () =>
  useMutation({
    mutationFn: setAuthorizedUserPassword,
  });

export const authUserCompleteOnboarding = async () => {
  const { customerId, authorizedUserId } = useBoundStore.getState().onboardingSlice;
  const token = useBoundStore.getState().getAuthUserAccessToken();

  const url = customerId
    ? `v3/onboarding/user/${customerId}/authorized/user/${authorizedUserId}/complete`
    : `v3/onboarding/authorized/user/${authorizedUserId}/complete`;

  const requestBody = customerId ? { token } : {};

  const res = await HttpService.post<CompleteOnboardingResponse>(url, requestBody);
  return res.data;
};

export const useAuthUserCompleteOnboarding = (
  options?: Omit<UseMutationOptions<CompleteOnboardingResponse, Error, void>, 'mutationFn'>
) =>
  useMutation({
    mutationFn: authUserCompleteOnboarding,
    ...options,
  });
